import { Component, OnDestroy, signal, effect, input } from "@angular/core";

/**
 * O2 Accordion Group Component
 *
 * Manages accordion items with single-open mode using automatic registration.
 * Items register themselves via dependency injection context.
 * Ensures only one item is open at a time.
 *
 * Features:
 * - Auto-reset to first item when parent sheet/dialog closes (optional)
 * - Pass sheet's isOpen signal to enable auto-reset behavior
 *
 * Usage:
 * <!-- Basic usage (no auto-reset) -->
 * <o2-accordion-group>
 *   <o2-accordion-item label="Info">
 *     <div>Content here</div>
 *   </o2-accordion-item>
 * </o2-accordion-group>
 *
 * <!-- With auto-reset when sheet closes -->
 * <o2-accordion-group [resetOnParentOpen]="sheetRef().isOpen()">
 *   <o2-accordion-item label="Info">
 *     <div>Content here</div>
 *   </o2-accordion-item>
 * </o2-accordion-group>
 */
@Component({
  selector: "o2-accordion-group",
  standalone: true,
  imports: [],
  template: `
    <div class="flex flex-col gap-2.5">
      <ng-content />
    </div>
  `,
})
export class O2AccordionGroupComponent implements OnDestroy {
  /**
   * Optional input: signal from parent sheet/dialog indicating open state
   * When parent closes (signal becomes false), accordion resets to first item
   * If not provided, no auto-reset behavior
   */
  public readonly resetOnParentOpen = input<boolean>();

  // Signal to track which item is currently open (by title)
  // Default: null (will be set to first item's title when registered)
  public readonly openItemTitle = signal<string | null>(null);

  // Map to track registered items by title
  private readonly _registeredItems = new Map<string, number>();
  private _firstItemTitle: string | null = null;

  constructor() {
    // Effect: reset to first item when parent closes (if resetOnParentOpen is provided)
    effect(() => {
      const parentOpen = this.resetOnParentOpen();

      // Only reset if input is provided and parent just closed
      if (parentOpen === false && this._firstItemTitle) {
        this.openItemTitle.set(this._firstItemTitle);
      }
    });
  }

  /**
   * Register a new item with its title
   * Called by child accordion items during initialization
   * Returns the registration order index
   */
  public registerItem(title: string): number {
    const index = this._registeredItems.size;
    this._registeredItems.set(title, index);

    // Set first item as open by default
    if (index === 0) {
      this._firstItemTitle = title;
      this.openItemTitle.set(title);
    }

    return index;
  }

  /**
   * Unregister an item when it's destroyed
   */
  public unregisterItem(title: string): void {
    this._registeredItems.delete(title);
  }

  /**
   * Check if an item is open by its title
   */
  public isItemOpen(title: string): boolean {
    return this.openItemTitle() === title;
  }

  /**
   * Toggle an item open/closed by its title
   * If the item is already open, keep it open (at least one must be open)
   * If another item is open, close it and open this one
   */
  public toggleItem(title: string): void {
    if (this.openItemTitle() === title) {
      // Keep the current item open - do not allow closing the last open item
      return;
    } else {
      this.openItemTitle.set(title);
    }
  }

  ngOnDestroy(): void {
    this._registeredItems.clear();
  }
}
